import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdoConnectionStatus,
  ArchitectureImportSummary,
  ProjectAdoMapping,
  adoIntegrationService,
} from '../../services/adoIntegrationService';
import { projectService } from '../../services/projectService';
import { ProjectItem } from '../../types/project';

import AdoConnectionPanel from './AdoConnectionPanel';
import { buildArchieImportsLink } from './adoLinks';
import {
  importStatusBadge,
  importStatusLabel,
  NON_TERMINAL_IMPORT_STATUSES,
} from './importStatus';
import './azure-devops.css';

interface ProjectRow {
  project: ProjectItem;
  mapping: ProjectAdoMapping | null;
  lastImport?: ArchitectureImportSummary;
  totalImports: number;
}

/**
 * Top-level Azure DevOps / Codie hub.
 *
 * Surfaces the per-project link to Azure DevOps (one Archie project ↔
 * one ADO project), how many architectures have been sent to Codie for
 * each, and recent activity across the workspace so users can see the
 * state of the integration at a glance.
 */
const AzureDevOpsHubPage: React.FC = () => {
  const [status, setStatus] = useState<AdoConnectionStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [imports, setImports] = useState<ArchitectureImportSummary[]>([]);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [mappings, setMappings] = useState<Map<number, ProjectAdoMapping>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshStatus = React.useCallback(async () => {
    setStatusLoading(true);
    try {
      setStatus(await adoIntegrationService.getConnectionStatus());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const refreshAll = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [conn, allProjects, allImports] = await Promise.all([
        adoIntegrationService.getConnectionStatus(),
        projectService.getProjects(),
        adoIntegrationService
          .listMyImports()
          .catch(() => [] as ArchitectureImportSummary[]),
      ]);
      setStatus(conn);
      setProjects(allProjects);
      setImports(allImports);

      // Mappings are independent per project; fetch in parallel and
      // tolerate 404/network errors per project rather than failing
      // the whole hub.
      const mappingResults = await Promise.all(
        allProjects.map(async (p) => {
          try {
            const m = await adoIntegrationService.getProjectAdoMapping(p.id);
            return [p.id, m] as const;
          } catch {
            return [p.id, null] as const;
          }
        }),
      );
      const nextMappings = new Map<number, ProjectAdoMapping>();
      for (const [pid, m] of mappingResults) {
        if (m) {nextMappings.set(pid, m);}
      }
      setMappings(nextMappings);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const projectRows: ProjectRow[] = React.useMemo(() => {
    const byProject = new Map<number, ArchitectureImportSummary[]>();
    for (const imp of imports) {
      const list = byProject.get(imp.project_id) ?? [];
      list.push(imp);
      byProject.set(imp.project_id, list);
    }
    return projects.map((project) => {
      const list = byProject.get(project.id) ?? [];
      return {
        project,
        mapping: mappings.get(project.id) ?? null,
        lastImport: list[0],
        totalImports: list.length,
      };
    });
  }, [projects, imports, mappings]);

  const summary = React.useMemo(() => {
    const linkedProjects = projectRows.filter((r) => r.mapping).length;
    const adoProjectsTouched = new Set<string>();
    for (const r of projectRows) {
      if (r.mapping) {
        adoProjectsTouched.add(
          `${r.mapping.ado_org}/${r.mapping.ado_project}`,
        );
      }
    }
    return {
      totalProjects: projectRows.length,
      linkedProjects,
      adoProjectsTouched: adoProjectsTouched.size,
      totalImports: imports.length,
    };
  }, [projectRows, imports]);

  const handleRefreshImport = React.useCallback(
    async (importId: number) => {
      try {
        const refreshed =
          await adoIntegrationService.refreshImportStatus(importId);
        setImports((prev) =>
          prev.map((row) => (row.id === refreshed.id ? refreshed : row)),
        );
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [],
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-onSurface-strong">
            Azure DevOps
          </h1>
          <p className="text-text-onSurface-subtle mt-1">
            Send Archie architectures to Codie and track when they land
            in your Azure DevOps backlog.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => { refreshAll(); }}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <AdoConnectionPanel
        status={status}
        loading={statusLoading}
        onChange={() => { refreshStatus(); }}
      />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          label="Archie projects"
          value={summary.totalProjects}
          hint="In this workspace"
        />
        <SummaryCard
          label="Linked to ADO"
          value={`${summary.linkedProjects} / ${summary.totalProjects}`}
          hint="Have a target project"
        />
        <SummaryCard
          label="ADO projects touched"
          value={summary.adoProjectsTouched}
          hint="Unique org/project pairs"
        />
        <SummaryCard
          label="Architectures sent"
          value={summary.totalImports}
          hint="By you, all-time"
        />
      </section>

      <section className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body gap-3">
          <div className="flex items-center justify-between">
            <h2 className="card-title text-lg">Projects</h2>
            <span className="text-xs opacity-60">
              {projectRows.length} project{projectRows.length === 1 ? '' : 's'}
            </span>
          </div>
          {loading ? (
            <div className="skeleton h-32 w-full" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Linked ADO target</th>
                    <th>Last sent</th>
                    <th>Items</th>
                    <th>Sends</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {projectRows.map((row) => (
                    <tr key={row.project.id}>
                      <td>
                        <div className="font-medium">{row.project.title}</div>
                        <div className="text-xs opacity-60">
                          {row.project.project_type}
                        </div>
                      </td>
                      <td>
                        {row.mapping ? (
                          <div>
                            <div className="font-medium text-text-onSurface-strong">
                              {row.mapping.ado_org}
                              {' / '}
                              {row.mapping.ado_project}
                            </div>
                            <div className="text-xs opacity-60">
                              Linked by {row.mapping.set_by}
                            </div>
                            <a
                              href={buildArchieImportsLink(
                                row.mapping.ado_org,
                                row.mapping.ado_project,
                              )}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="link link-primary text-xs"
                            >
                              Archie Imports in ADO ↗
                            </a>
                          </div>
                        ) : (
                          <span className="badge badge-ghost">Not linked</span>
                        )}
                      </td>
                      <td>
                        {row.lastImport
                          ? new Date(
                            row.lastImport.submitted_at,
                          ).toLocaleString()
                          : '—'}
                      </td>
                      <td>
                        {row.lastImport
                          ? `${row.lastImport.work_item_count}`
                          : '—'}
                      </td>
                      <td>{row.totalImports}</td>
                      <td className="text-right">
                        <Link
                          to={`/project/${row.project.id}/azure-devops`}
                          className="btn btn-primary btn-sm main-button"
                        >
                          {row.mapping ? 'Open' : 'Link & send'}
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {projectRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center opacity-60 py-6">
                        No projects yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body gap-3">
          <h2 className="card-title text-lg">Recent activity</h2>
          {imports.length === 0 ? (
            <div className="alert">
              Nothing sent yet. Open a project and use the “Send to Codie”
              page to publish an architecture.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Project</th>
                    <th>ADO target</th>
                    <th>Sent by</th>
                    <th>Codie id</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {imports.slice(0, 10).map((imp) => (
                    <tr key={imp.id}>
                      <td>
                        {new Date(imp.submitted_at).toLocaleString()}
                      </td>
                      <td>
                        <Link
                          to={`/project/${imp.project_id}/azure-devops`}
                          className="link link-primary"
                        >
                          #{imp.project_id}
                        </Link>
                      </td>
                      <td>
                        <a
                          href={buildArchieImportsLink(
                            imp.ado_org,
                            imp.ado_project,
                          )}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="link link-primary font-mono text-xs"
                          title="Open the Archie Imports tab in Azure DevOps"
                        >
                          {imp.ado_org}/{imp.ado_project}
                        </a>
                      </td>
                      <td>
                        <span className="text-xs">{imp.created_by}</span>
                      </td>
                      <td>
                        <span className="font-mono text-xs">
                          {imp.codie_import_id}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${importStatusBadge(imp.status)}`}
                        >
                          {importStatusLabel(imp.status)}
                        </span>
                      </td>
                      <td>{imp.work_item_count}</td>
                      <td>
                        {NON_TERMINAL_IMPORT_STATUSES.has(imp.status) && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs"
                            title="Check Codie for the latest status"
                            onClick={() => { handleRefreshImport(imp.id); }}
                          >
                            Refresh
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

interface SummaryCardProps {
  label: string;
  value: string | number;
  hint?: string;
}

/** Single key-figure tile used at the top of the Hub page. */
const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, hint }) => (
  <div className="rounded-lg border border-border-medium bg-background-surface shadow-sm px-4 py-3">
    <div className="text-xs uppercase tracking-wide text-text-onSurface-subtle">
      {label}
    </div>
    <div className="text-2xl font-bold text-text-onSurface-strong mt-1">
      {value}
    </div>
    {hint && (
      <div className="text-xs text-text-onSurface-subtle mt-1">{hint}</div>
    )}
  </div>
);

export default AzureDevOpsHubPage;
